// src/hooks/useAuth.js
import { useState, useEffect } from 'react';
import { PINS, ROLES, STORAGE_KEYS } from '../utils/constants';

/**
 * Custom hook for authentication management
 */
export const useAuth = () => {
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

 // ADD THESE DEBUG LINES:
  console.log('🔍 PINS object:', PINS);
  console.log('🔍 STORAGE_KEYS:', STORAGE_KEYS);

  // Load saved role on mount
  useEffect(() => {
    const savedRole = localStorage.getItem(STORAGE_KEYS.USER_ROLE);
    if (savedRole) {
      setUserRole(savedRole);
    }
    setIsLoading(false);
  }, []);

  /**
   * Validate PIN and login user
   */
const login = async (pin) => {
  const { verifyLogin } = await import('../services/authService');
  
  try {
    // Verify PIN against admin, staff, and Firestore drivers
    const user = await verifyLogin(pin);
    
    // Map the role to the app's role system
    let role = null;
    if (user.role === 'admin') role = ROLES.ADMIN;
    else if (user.role === 'staff') role = ROLES.STAFF;
    else if (user.role === 'driver') {
      // For drivers from Firestore, use their ID as the role
      role = user.id;
    }
    
    if (!role) {
      throw new Error('Invalid PIN');
    }

    setUserRole(role);
    localStorage.setItem(STORAGE_KEYS.USER_ROLE, role);
    return role;
  } catch (error) {
    throw new Error(error.message || 'Invalid PIN');
  }
};
  /**
   * Logout user
   */
  const logout = () => {
    setUserRole(null);
    localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    return userRole === role;
  };

  /**
   * Check if user is admin
   */
  const isAdmin = () => {
    return userRole === ROLES.ADMIN;
  };

  /**
   * Check if user is staff
   */
  const isStaff = () => {
    return userRole === ROLES.STAFF || userRole === ROLES.ADMIN;
  };

  /**
   * Check if user is driver
   */
 const isDriver = () => {
  // Check if role is a driver (either hardcoded driver1/driver2 or Firestore driver ID)
  return userRole && (userRole === ROLES.DRIVER1 || userRole === ROLES.DRIVER2 || 
         (userRole.length > 10)); // Firestore IDs are long strings
}


  /**
   * Get driver ID if user is a driver
   */
const getDriverId = () => {
  // Return the role directly if it's a driver ID
  if (isDriver()) {
    return userRole;
  }
  return null;
};

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return userRole !== null;
  };

  return {
    userRole,
    isLoading,
    login,
    logout,
    hasRole,
    isAdmin,
    isStaff,
    isDriver,
    getDriverId,
    isAuthenticated
  };
};

export default useAuth;